import { MGPOptional, Utils } from '@everyboard/lib';
import { AlignmentHeuristic, AlignmentStatus } from '../../jscaip/AI/AlignmentHeuristic';
import { BoardValue } from '../../jscaip/AI/BoardValue';
import { HexaDirection } from '../../jscaip/HexaDirection';
import { PlayerOrNone } from '../../jscaip/Player';
import { SixRules } from './SixRules';
export class SixHeuristic extends AlignmentHeuristic {
    VERBOSE = false;
    currentVictorySource;
    getBoardValue(node, config) {
        const move = node.previousMove;
        const state = node.gameState;
        const previousPlayer = state.getPreviousPlayer();
        const victoryValue = BoardValue.getVictoryValueOf(previousPlayer);
        let shapeInfo = {
            status: AlignmentStatus.NOTHING,
            victory: MGPOptional.empty(),
            preVictory: MGPOptional.empty(),
            sum: 0,
        };
        if (move.isPresent()) {
            shapeInfo = this.calculateBoardValue(move.get(), state);
        }
        if (shapeInfo.status === AlignmentStatus.VICTORY) {
            return BoardValue.of(victoryValue);
        }
        if (SixRules.get().isInDropPhase(state, config) === false) {
            const pieces = state.countPiecesOnBoard();
            return BoardValue.ofPlayerNumberMap(pieces);
        }
        if (shapeInfo.status === AlignmentStatus.PRE_VICTORY) {
            return BoardValue.of(BoardValue.getPreVictoryValueOf(previousPlayer));
        }
        return BoardValue.of(shapeInfo.sum * previousPlayer.getScoreModifier());
    }
    startSearchingVictorySources() {
        this.currentVictorySource = {
            typeSource: 'LINE',
            index: -1,
        };
    }
    hasNextVictorySource() {
        return this.currentVictorySource.typeSource !== 'CIRCLE' ||
            this.currentVictorySource.index !== 5;
    }
    getNextVictorySource() {
        const source = this.currentVictorySource;
        if (source.index === 5) {
            let newType;
            switch (this.currentVictorySource.typeSource) {
                case 'LINE':
                    newType = 'TRIANGLE_CORNER';
                    break;
                case 'TRIANGLE_CORNER':
                    newType = 'TRIANGLE_EDGE';
                    break;
                default:
                    newType = 'CIRCLE';
                    break;
            }
            this.currentVictorySource = {
                typeSource: newType,
                index: 0,
            };
        }
        else {
            let increment = 1;
            if (this.currentVictorySource.typeSource === 'LINE') {
                increment = 2;
            }
            this.currentVictorySource = {
                typeSource: this.currentVictorySource.typeSource,
                index: this.currentVictorySource.index + increment,
            };
        }
        return this.currentVictorySource;
    }
    searchVictoryOnly(victorySource, move, state) {
        const lastDrop = move.landing;
        switch (victorySource.typeSource) {
            case 'LINE':
                return this.searchVictoryOnlyForLine(victorySource.index, lastDrop, state);
            case 'CIRCLE':
                return this.searchVictoryOnlyForCircle(victorySource.index, lastDrop, state);
            case 'TRIANGLE_CORNER':
                return this.searchVictoryOnlyForTriangleCorner(victorySource.index, lastDrop, state);
            default:
                return this.searchVictoryOnlyForTriangleEdge(victorySource.index, lastDrop, state);
        }
    }
    searchVictoryOnlyForCircle(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        const initialDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(initialDirection, 1);
        while (testedCoords.length < 6) {
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return {
                    status: AlignmentStatus.PRE_VICTORY,
                    victory: MGPOptional.empty(),
                    preVictory: MGPOptional.empty(),
                    sum: 0,
                };
            }
            const dirIndex = (index + testedCoords.length) % 6;
            testedCoords.push(testCoord);
            const dir = HexaDirection.factory.all[dirIndex];
            testCoord = testCoord.getNext(dir, 1);
        }
        return {
            status: AlignmentStatus.VICTORY,
            victory: MGPOptional.of(testedCoords),
            preVictory: MGPOptional.empty(),
            sum: 0,
        };
    }
    searchVictoryOnlyForLine(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let dir = HexaDirection.factory.all[index];
        let testCoord = lastDrop.getNext(dir, 1);
        const victory = [lastDrop];
        let twoDirectionCovered = false;
        while (victory.length < 6) {
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece === previousPlayer) {
                victory.push(testCoord);
            }
            else {
                if (twoDirectionCovered) {
                    return {
                        status: AlignmentStatus.PRE_VICTORY,
                        victory: MGPOptional.empty(),
                        preVictory: MGPOptional.empty(),
                        sum: 0,
                    };
                }
                else {
                    twoDirectionCovered = true;
                    dir = dir.getOpposite();
                    testCoord = testCoord.getNext(dir, victory.length - 1);
                }
            }
            testCoord = testCoord.getNext(dir, 1);
        }
        return {
            status: AlignmentStatus.VICTORY,
            victory: MGPOptional.of(victory),
            preVictory: MGPOptional.empty(),
            sum: 0,
        };
    }
    searchVictoryOnlyForTriangleCorner(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let edgeDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        while (testedCoords.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return {
                    status: AlignmentStatus.PRE_VICTORY,
                    victory: MGPOptional.empty(),
                    preVictory: MGPOptional.empty(),
                    sum: 0,
                };
            }
            if (testedCoords.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + testedCoords.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            testedCoords.push(testCoord);
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return {
            status: AlignmentStatus.VICTORY,
            victory: MGPOptional.of(testedCoords),
            preVictory: MGPOptional.empty(),
            sum: 0,
        };
    }
    searchVictoryOnlyForTriangleEdge(index, lastDrop, state) {
        const previousPlayer = state.getPreviousPlayer();
        let edgeDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        while (testedCoords.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece !== previousPlayer) {
                return {
                    status: AlignmentStatus.PRE_VICTORY,
                    victory: MGPOptional.empty(),
                    preVictory: MGPOptional.empty(),
                    sum: 0,
                };
            }
            testedCoords.push(testCoord);
            if (testedCoords.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + testedCoords.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return {
            status: AlignmentStatus.VICTORY,
            victory: MGPOptional.of(testedCoords),
            preVictory: MGPOptional.empty(),
            sum: 0,
        };
    }
    getBoardInfo(victorySource, move, state, boardInfo) {
        const lastDrop = move.landing;
        switch (victorySource.typeSource) {
            case 'CIRCLE':
                return this.getBoardInfoForCircle(victorySource.index, lastDrop, state, boardInfo);
            case 'LINE':
                return this.getBoardInfoForLine(victorySource.index, lastDrop, state, boardInfo);
            case 'TRIANGLE_CORNER':
                return this.getBoardInfoForTriangleCorner(victorySource.index, lastDrop, state, boardInfo);
            default:
                return this.getBoardInfoForTriangleEdge(victorySource.index, lastDrop, state, boardInfo);
        }
    }
    getBoardInfoForCircle(index, lastDrop, state, boardInfo) {
        const previousOpponent = state.getPreviousOpponent();
        const initialDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(initialDirection, 1);
        let subSum = 0;
        let lastEmpty = MGPOptional.empty();
        while (testedCoords.length < 6) {
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece === previousOpponent) {
                return boardInfo; // nothing to add here
            }
            const dirIndex = (index + testedCoords.length) % 6;
            testedCoords.push(testCoord);
            const dir = HexaDirection.factory.all[dirIndex];
            if (testedPiece.isNone()) {
                subSum += 0.16; // roughly 1/6
                lastEmpty = MGPOptional.of(testCoord);
            }
            else {
                subSum++;
            }
            testCoord = testCoord.getNext(dir, 1);
        }
        return this.getBoardInfoResult(subSum, lastEmpty, testedCoords, boardInfo);
    }
    getBoardInfoResult(subSum, lastEmpty, testedCoords, boardInfo) {
        let preVictory = boardInfo.preVictory;
        if (subSum === 4.16) {
            // We found 5 pieces aligned and one space, so that space is a preVictory coord
            if (preVictory.isPresent() && (preVictory.equals(lastEmpty) === false)) {
                return {
                    status: AlignmentStatus.PRE_VICTORY,
                    victory: MGPOptional.empty(),
                    preVictory: MGPOptional.empty(),
                    sum: 0,
                };
            }
            else {
                preVictory = lastEmpty;
            }
        }
        else if (subSum === 5) {
            return {
                status: AlignmentStatus.VICTORY,
                victory: MGPOptional.of(testedCoords),
                preVictory: MGPOptional.empty(),
                sum: 0,
            };
        }
        return {
            status: boardInfo.status,
            victory: MGPOptional.empty(),
            preVictory,
            sum: boardInfo.sum + subSum,
        };
    }
    getBoardInfoForLine(index, lastDrop, state, boardInfo) {
        const dir = HexaDirection.factory.all[index];
        let testedCoord = lastDrop.getPrevious(dir, 5);
        let testedCoords = [];
        let encountered = [];
        let lastEmpty = MGPOptional.empty();
        for (let i = 0; i < 6; i++) {
            const empty = this.updateEncounterAndReturnLastEmpty(state, testedCoord, encountered);
            if (empty.isPresent()) {
                lastEmpty = empty;
            }
            testedCoords.push(testedCoord);
            testedCoord = testedCoord.getNext(dir, 1);
        }
        let status = boardInfo.status;
        let preVictory = boardInfo.preVictory;
        let nbTested = 0;
        let finalSubSum = 0;
        while (nbTested < 6) {
            const subSum = encountered.reduce((a, b) => a + b);
            if (subSum === 5.16 && status === AlignmentStatus.NOTHING) {
                if (preVictory.isPresent()) {
                    Utils.assert(preVictory.equals(lastEmpty) === false, 'Impossible to have point aligned with different line to a same point');
                    status = AlignmentStatus.PRE_VICTORY;
                }
                else {
                    preVictory = lastEmpty;
                }
            }
            finalSubSum = Math.max(finalSubSum, subSum);
            testedCoords = testedCoords.slice(1, 6);
            testedCoords.push(testedCoord);
            encountered = encountered.slice(1, 6);
            const newEmpty = this.updateEncounterAndReturnLastEmpty(state, testedCoord, encountered);
            if (newEmpty.isPresent()) {
                lastEmpty = newEmpty;
            }
            nbTested++;
            testedCoord = testedCoord.getNext(dir, 1);
        }
        const newBoardInfo = {
            status,
            victory: MGPOptional.of(testedCoords),
            preVictory,
            sum: finalSubSum,
        };
        return this.getBoardInfoResult(finalSubSum, lastEmpty, testedCoords, newBoardInfo);
    }
    updateEncounterAndReturnLastEmpty(state, testedCoord, encountered) {
        const previousOpponent = state.getPreviousOpponent();
        switch (state.getPieceAt(testedCoord)) {
            case previousOpponent:
                encountered.push(-7);
                // just enough to make sum negative when opponent encountered
                return MGPOptional.empty();
            case PlayerOrNone.NONE:
                encountered.push(0.16);
                return MGPOptional.of(testedCoord);
            default:
                encountered.push(1);
                return MGPOptional.empty();
        }
    }
    getBoardInfoForTriangleCorner(index, lastDrop, state, boardInfo) {
        const previousOpponent = state.getPreviousOpponent();
        let edgeDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        let subSum = 0;
        let lastEmpty = MGPOptional.empty();
        while (testedCoords.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece === previousOpponent) {
                return boardInfo;
            }
            if (testedPiece.isNone()) {
                subSum += 0.16; // rougly 1/6
                lastEmpty = MGPOptional.of(testCoord);
            }
            else {
                subSum++;
            }
            if (testedCoords.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + testedCoords.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            testedCoords.push(testCoord);
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return this.getBoardInfoResult(subSum, lastEmpty, testedCoords, boardInfo);
    }
    getBoardInfoForTriangleEdge(index, lastDrop, state, boardInfo) {
        const previousOpponent = state.getPreviousOpponent();
        let edgeDirection = HexaDirection.factory.all[index];
        const testedCoords = [lastDrop];
        let testCoord = lastDrop.getNext(edgeDirection, 1);
        let subSum = 0;
        let lastEmpty = MGPOptional.empty();
        while (testedCoords.length < 6) {
            // Testing the corner
            const testedPiece = state.getPieceAt(testCoord);
            if (testedPiece === previousOpponent) {
                return boardInfo;
            }
            if (testedPiece.isNone()) {
                subSum += 0.16; // rougly 1/6
                lastEmpty = MGPOptional.of(testCoord);
            }
            else {
                subSum++;
            }
            testedCoords.push(testCoord);
            if (testedCoords.length % 2 === 0) {
                // reached a corner, let's turn
                const dirIndex = (index + testedCoords.length) % 6;
                edgeDirection = HexaDirection.factory.all[dirIndex];
            }
            testCoord = testCoord.getNext(edgeDirection, 1);
        }
        return this.getBoardInfoResult(subSum, lastEmpty, testedCoords, boardInfo);
    }
}
